
import { clampNumber, LCA_STAGES, TRANSPORT_MODES } from "./lcaTypes";
import { convertInputToDeclaredQuantity } from "./unitConversion";

export function computeInstallations(buildingLifeYears, componentServiceLifeYears) {
  const b = clampNumber(buildingLifeYears, 0);
  const s = clampNumber(componentServiceLifeYears, 0);

  if (!(b > 0)) return 1;
  if (!(s > 0)) return 1;
  if (s >= b) return 1;

  // installs includes initial plus replacements
  return Math.ceil(b / s);
}

function extractNumeric(value) {
  if (value == null) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") {
    const n = Number(value.replace(/[^0-9eE.+-]/g, ""));
    return Number.isFinite(n) ? n : null;
  }
  if (typeof value === "object") {
    const candidate =
      value.value ??
      value.amount ??
      value.mean ??
      value.total ??
      value.quantity ??
      value.result ??
      null;
    if (candidate != null) return extractNumeric(candidate);
  }
  return null;
}

function getPropsMap(materialProperties) {
  if (!materialProperties) return null;
  if (materialProperties.properties && typeof materialProperties.properties === "object") {
    return materialProperties.properties;
  }
  if (typeof materialProperties === "object") return materialProperties;
  return null;
}

function pickPropertyValue(propsMap, keys) {
  if (!propsMap || typeof propsMap !== "object") return null;
  const entries = Object.entries(propsMap);
  for (const target of keys) {
    const found = entries.find(([k]) => k.toLowerCase() === target.toLowerCase());
    if (found) return extractNumeric(found[1]);
  }
  for (const target of keys) {
    const found = entries.find(([k]) => k.toLowerCase().includes(target.toLowerCase()));
    if (found) return extractNumeric(found[1]);
  }
  return null;
}

function getEpdFallbacks(epd) {
  const propsMap = getPropsMap(epd?.materialProperties);
  if (!propsMap) return { densityKgPerM3: null, thicknessM: null, massPerAreaKgPerM2: null };
  const densityKgPerM3 = pickPropertyValue(propsMap, [
    "densityKgPerM3",
    "bulkDensity",
    "grossDensity",
    "density"
  ]);
  const thicknessM = pickPropertyValue(propsMap, ["thicknessM", "layerThickness", "thickness"]);
  const massPerAreaKgPerM2 = pickPropertyValue(propsMap, [
    "massPerAreaKgPerM2",
    "surfaceWeight",
    "arealDensity",
    "areaDensity"
  ]);
  return { densityKgPerM3, thicknessM, massPerAreaKgPerM2 };
}

const GENERIC_DENSITY_KG_PER_M3 = [
  { key: "concrete", density: 2400 },
  { key: "reinforced concrete", density: 2500 },
  { key: "cement", density: 1440 },
  { key: "steel", density: 7850 },
  { key: "aluminum", density: 2700 },
  { key: "copper", density: 8960 },
  { key: "glass", density: 2500 },
  { key: "brick", density: 1800 },
  { key: "masonry", density: 2000 },
  { key: "stone", density: 2600 },
  { key: "timber", density: 600 },
  { key: "wood", density: 600 },
  { key: "gypsum", density: 850 },
  { key: "insulation", density: 30 },
  { key: "asphalt", density: 2300 },
  { key: "plastic", density: 950 }
];

function getGenericDensityKgPerM3(item) {
  const name = `${item?.name || ""} ${item?.subcategory || ""}`.toLowerCase();
  const match = GENERIC_DENSITY_KG_PER_M3.find((entry) => name.includes(entry.key));
  return match ? match.density : null;
}

const GENERIC_AREAL_DENSITY_KG_PER_M2 = [
  { key: "gypsum board", density: 10 },
  { key: "drywall", density: 10 },
  { key: "plywood", density: 7 },
  { key: "osb", density: 12 },
  { key: "glass", density: 25 },
  { key: "insulation", density: 3 },
  { key: "roofing membrane", density: 5 },
  { key: "carpet", density: 2.5 },
  { key: "ceramic tile", density: 20 },
  { key: "vinyl", density: 3 },
  { key: "cladding", density: 18 },
  { key: "flooring", density: 15 },
  { key: "plaster", density: 12 },
  { key: "acoustic panel", density: 8 },
  { key: "metal sheet", density: 12 }
];

function getGenericArealDensityKgPerM2(item) {
  const name = `${item?.name || ""} ${item?.subcategory || ""}`.toLowerCase();
  const match = GENERIC_AREAL_DENSITY_KG_PER_M2.find((entry) => name.includes(entry.key));
  return match ? match.density : null;
}

function estimateMassKg({
  item,
  epd,
  declaredQuantity,
  declaredUnit,
  densityKgPerM3,
  thicknessM,
  massPerAreaKgPerM2
}) {
  // If EPD declared unit already mass, use directly
  if (declaredUnit === "kg") return declaredQuantity;
  if (declaredUnit === "t") return declaredQuantity * 1000;

  const qty = clampNumber(item?.quantity, 0);
  const qtyUnit = item?.quantityUnit || declaredUnit;
  const density = clampNumber(densityKgPerM3 ?? item?.densityKgPerM3, 0);
  const thickness = clampNumber(thicknessM ?? item?.thicknessM, 0);
  const areal = clampNumber(massPerAreaKgPerM2, 0);

  // If declared unit is m3, use density
  if (declaredUnit === "m3" && density > 0) return declaredQuantity * density;
  // If declared unit is m2, prefer areal density if available
  if (declaredUnit === "m2") {
    if (areal > 0) return declaredQuantity * areal;
    if (density > 0 && thickness > 0) return declaredQuantity * thickness * density;
  }

  // Fall back to user-entered quantity unit if it’s mass or convertible
  if (qty > 0) {
    if (qtyUnit === "kg") return qty;
    if (qtyUnit === "t") return qty * 1000;
    if (qtyUnit === "m3" && density > 0) return qty * density;
    if (qtyUnit === "m2") {
      if (areal > 0) return qty * areal;
      if (density > 0 && thickness > 0) return qty * thickness * density;
    }
  }

  return null;
}

export function computeComponent({
  item,
  epd,
  settings
}) {
  const buildingLifeYears = clampNumber(settings?.buildingLifeYears, 60);
  const buildingArea = clampNumber(settings?.buildingArea, 0);
  const includeModuleD = !!settings?.includeModuleD;
  const includeModuleB6 = settings?.includeModuleB6 ?? true;
  const countReplacementEndOfLife = settings?.countReplacementEndOfLife ?? true;

  const quantity = clampNumber(item?.quantity, 0);
  const quantityUnit = item?.quantityUnit || "m3";
  const serviceLifeYears = clampNumber(item?.serviceLifeYears, 0);
  const epdFallbacks = getEpdFallbacks(epd);
  const itemDensity = Number.isFinite(Number(item?.densityKgPerM3)) ? Number(item?.densityKgPerM3) : null;
  const itemThickness = Number.isFinite(Number(item?.thicknessM)) ? Number(item?.thicknessM) : null;
  const genericDensity = getGenericDensityKgPerM3(item);
  const genericArealDensity = getGenericArealDensityKgPerM2(item);
  const densityKgPerM3 = clampNumber(
    itemDensity > 0 ? itemDensity : epdFallbacks.densityKgPerM3 ?? genericDensity,
    0
  );
  const thicknessM = clampNumber(itemThickness > 0 ? itemThickness : epdFallbacks.thicknessM, 0);
  const massPerAreaKgPerM2 = clampNumber(epdFallbacks.massPerAreaKgPerM2 ?? genericArealDensity, 0);
  const customFactor = clampNumber(item?.customFactor, 0);
  const transport = item?.transport || null;

  const result = {
    ok: false,
    missing: [],
    conversion: null,
    installations: 1,
    stageImpacts: {},
    total: 0,
    transportOk: false,
    transportMissing: [],
    transportImpact: null,
    inputMeta: {
      densityKgPerM3,
      thicknessM,
      massPerAreaKgPerM2,
      usedEpdDensity: false,
      usedEpdThickness: false,
      usedGenericDensity: false,
      usedEpdArealDensity: false,
      usedGenericArealDensity: false
    }
  };

  if (item?.category === "energy") {
    const energyUseKwhPerM2Yr = clampNumber(item?.energyUseKwhPerM2Yr, 0);
    const gridIntensityKgPerKwh = clampNumber(item?.gridIntensityKgPerKwh, 0);
    const missing = [];
    if (!includeModuleB6) {
      result.stageImpacts = { B6: 0 };
      result.total = 0;
      result.ok = true;
      return result;
    }
    if (!(energyUseKwhPerM2Yr > 0)) missing.push("Energy use");
    if (!(gridIntensityKgPerKwh > 0)) missing.push("Grid intensity");
    if (!(buildingArea > 0)) missing.push("Building area");
    if (!(buildingLifeYears > 0)) missing.push("Building life");
    if (missing.length > 0) {
      result.missing = missing;
      return result;
    }

    const impactPerM2Yr = energyUseKwhPerM2Yr * gridIntensityKgPerKwh;
    const total = impactPerM2Yr * buildingArea * buildingLifeYears;
    result.stageImpacts = { B6: total };
    result.total = total;
    result.ok = true;
    return result;
  }

  const transportMissing = [];
  const pushTransportMissing = (label) => {
    if (!transportMissing.includes(label)) transportMissing.push(label);
  };
  if (!epd) pushTransportMissing("EPD");
  if (!(quantity > 0)) pushTransportMissing("Quantity");
  const distanceKm = clampNumber(transport?.distanceKm, 0);
  const legs = Array.isArray(transport?.legs) ? transport.legs : [];
  if (!(distanceKm > 0)) pushTransportMissing("Distance");
  if (legs.length === 0) pushTransportMissing("Legs");

  if (!epd) {
    result.missing.push("EPD");
    result.transportMissing = transportMissing;
    return result;
  }

  if (!(quantity > 0)) {
    result.missing.push("Quantity");
    result.transportMissing = transportMissing;
    return result;
  }

  const conv = convertInputToDeclaredQuantity({
    inputValue: quantity,
    inputUnit: quantityUnit,
    declaredUnit: epd.declaredUnit,
    densityKgPerM3,
    massPerAreaKgPerM2,
    thicknessM,
    customFactor
  });

  result.conversion = conv;

  if (conv.ok) {
    const uses = { density: false, thickness: false, areal: false };
    const declaredUnit = epd.declaredUnit;
    const inputUnit = quantityUnit;
    if (declaredUnit !== inputUnit) {
      if (declaredUnit === "m3") {
        if (inputUnit === "kg" || inputUnit === "t") uses.density = true;
        if (inputUnit === "m2") uses.thickness = true;
      }
      if (declaredUnit === "m2") {
        if (inputUnit === "m3") uses.thickness = true;
        if (inputUnit === "kg" || inputUnit === "t") {
          if (massPerAreaKgPerM2 > 0) uses.areal = true;
          else {
            uses.density = true;
            uses.thickness = true;
          }
        }
      }
      if (declaredUnit === "kg") {
        if (inputUnit === "m3") uses.density = true;
        if (inputUnit === "m2") {
          if (massPerAreaKgPerM2 > 0) uses.areal = true;
          else {
            uses.density = true;
            uses.thickness = true;
          }
        }
      }
    }

    result.inputMeta.usedEpdDensity =
      uses.density && !(itemDensity > 0) && Number.isFinite(epdFallbacks.densityKgPerM3);
    result.inputMeta.usedGenericDensity =
      uses.density &&
      !(itemDensity > 0) &&
      !Number.isFinite(epdFallbacks.densityKgPerM3) &&
      Number.isFinite(genericDensity);
    result.inputMeta.usedEpdThickness =
      uses.thickness && !(itemThickness > 0) && Number.isFinite(epdFallbacks.thicknessM);
    result.inputMeta.usedEpdArealDensity = uses.areal && Number.isFinite(epdFallbacks.massPerAreaKgPerM2);
    result.inputMeta.usedGenericArealDensity =
      uses.areal &&
      !Number.isFinite(epdFallbacks.massPerAreaKgPerM2) &&
      Number.isFinite(genericArealDensity);
  }

  if (!conv.ok) {
    if (conv.needs?.density) result.missing.push("Density");
    if (conv.needs?.thickness) result.missing.push("Thickness");
    if (conv.needs?.customFactor) result.missing.push("Custom factor");
    if (conv.needs?.density) pushTransportMissing("Density");
    if (conv.needs?.thickness) pushTransportMissing("Thickness");
    if (conv.needs?.customFactor) pushTransportMissing("Custom factor");
    result.transportMissing = transportMissing;
    return result;
  }

  const installations = computeInstallations(buildingLifeYears, serviceLifeYears);
  result.installations = installations;

  // Multipliers
  const installMult = installations;
  const eolMult = countReplacementEndOfLife ? installations : 1;

  const q = conv.declaredQuantity;
  const declaredUnit = epd.declaredUnit;

  const stageImpacts = {};
  for (const s of LCA_STAGES) {
    const factor = clampNumber(epd?.gwp?.[s.key], 0);

    if (s.key === "D") {
      if (!includeModuleD) {
        stageImpacts[s.key] = 0;
        continue;
      }
      // D often negative or positive, apply end of life multiplier
      stageImpacts[s.key] = q * factor * eolMult;
      continue;
    }

    if (s.key === "C1C4") {
      stageImpacts[s.key] = q * factor * eolMult;
      continue;
    }

    if (s.key === "B4") {
      // If epd provides B4 directly, use it per declared unit per installation
      // If you prefer to compute B4 from A modules for replacements only, you can change this
      stageImpacts[s.key] = q * factor * installMult;
      continue;
    }

    // A modules happen per installation
    stageImpacts[s.key] = q * factor * installMult;
  }

  // Transport A4 addition (kg CO2e)
  result.transportMissing = transportMissing;
  const modeFactors = new Map((TRANSPORT_MODES || []).map((m) => [m.key, clampNumber(m.factorKgPerTkm, 0)]));

  const massKg = estimateMassKg({
    item,
    epd,
    declaredQuantity: q,
    declaredUnit,
    densityKgPerM3,
    thicknessM,
    massPerAreaKgPerM2
  });

  if (distanceKm > 0 && legs.length > 0 && massKg != null) {
    const massTons = massKg / 1000;
    let remaining = distanceKm;
    let transportImpact = 0;
    for (const leg of legs) {
      if (remaining <= 0) break;
      const km = Math.max(0, Math.min(remaining, clampNumber(leg?.km, 0)));
      const f = modeFactors.get(leg?.mode);
      if (km > 0 && f > 0) {
        transportImpact += km * massTons * f * installMult;
        remaining -= km;
      }
    }
    if (!stageImpacts.A4) stageImpacts.A4 = 0;
    stageImpacts.A4 += transportImpact;
    result.transportImpact = transportImpact;
    result.transportOk = true;
  } else if (massKg == null) {
    pushTransportMissing("Mass conversion");
    result.transportMissing = transportMissing;
  }

  result.stageImpacts = stageImpacts;
  result.total = Object.values(stageImpacts).reduce((a, b) => a + clampNumber(b, 0), 0);
  result.ok = true;

  return result;
}

export function computeProject({
  items,
  epdsById,
  settings
}) {
  const byItemId = {};
  const byCategory = {};
  const stageTotals = {};
  let total = 0;

  for (const item of items || []) {
    const epd = item?.epdId ? epdsById?.[item.epdId] : null;
    const comp = computeComponent({ item, epd, settings });
    byItemId[item.id] = comp;

    if (comp.ok) {
      total += comp.total;

      const cat = item.category || "unknown";
      if (!byCategory[cat]) byCategory[cat] = { total: 0, itemIds: [] };
      byCategory[cat].total += comp.total;
      byCategory[cat].itemIds.push(item.id);

      for (const [k, v] of Object.entries(comp.stageImpacts || {})) {
        stageTotals[k] = (stageTotals[k] || 0) + clampNumber(v, 0);
      }
    }
  }

  return {
    total,
    byItemId,
    byCategory,
    stageTotals
  };
}
